import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceArea, ReferenceLine
} from 'recharts';
import { Button, Card, Form } from 'react-bootstrap';
import { formatChartTime } from '../../utils/timeUtils';
import './TimeSeriesChart.css';

/**
 * Time Series Chart component for displaying message counts over time
 * Uses recharts for visualization
 */
const TimeSeriesChart = ({
  data,
  sources = [],
  sourceColors = {},
  onPointClick,
  onRangeSelect,
  onClearSelection,
  selectedSourceIds = [],
  bucketDuration = 10000, // Dynamic bucket duration from parent
  selectedDuration = 5 * 60 * 1000,
  onDurationChange,
  durationOptions = [],
}) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [dragRange, setDragRange] = useState(null);
  const [chartEndTime, setChartEndTime] = useState(() => (
    Math.floor(Date.now() / bucketDuration) * bucketDuration
  ));
  const didDragRef = useRef(false);
  const previousBucketDurationRef = useRef(bucketDuration);

  useEffect(() => {
    let clockTimeout;

    const updateChartEndTime = () => {
      const now = Date.now();
      setChartEndTime(Math.floor(now / bucketDuration) * bucketDuration);

      const nextBoundaryDelay = bucketDuration - (now % bucketDuration) + 20;
      clockTimeout = setTimeout(updateChartEndTime, nextBoundaryDelay);
    };

    updateChartEndTime();

    return () => clearTimeout(clockTimeout);
  }, [bucketDuration]);

  // Format data for chart - always show full width of selected window
  const chartData = useMemo(() => {
    const currentInterval = chartEndTime;
    const windowEnd = currentInterval + bucketDuration;
    const cutoff = currentInterval - selectedDuration;
    const dataByTimestamp = new Map();

    const createEmptyPoint = (timestamp) => {
      const point = {
        timestamp,
        time: formatChartTime(timestamp),
        total: 0,
        bucketDuration
      };

      sources.forEach(source => {
        point[source.id] = 0;
      });

      return point;
    };

    if (data && data.length > 0) {
      data.forEach(dataPoint => {
        if (dataPoint.timestamp >= cutoff && dataPoint.timestamp < windowEnd) {
          const bucketTimestamp = Math.floor(dataPoint.timestamp / bucketDuration) * bucketDuration;
          const existingPoint = dataByTimestamp.get(bucketTimestamp) || createEmptyPoint(bucketTimestamp);

          sources.forEach(source => {
            existingPoint[source.id] = (existingPoint[source.id] || 0) + (dataPoint[source.id] || 0);
          });
          existingPoint.total += dataPoint.total || 0;

          dataByTimestamp.set(bucketTimestamp, existingPoint);
        }
      });
    }

    // Generate complete time window with all intervals (including current)
    const fullTimeWindow = [];
    for (let timestamp = cutoff; timestamp <= currentInterval; timestamp += bucketDuration) {
      fullTimeWindow.push({
        ...createEmptyPoint(timestamp),
        ...dataByTimestamp.get(timestamp)
      });
    }

    // Minimal debug logging only for critical issues
    if (process.env.NODE_ENV === 'development' && fullTimeWindow.length === 0 && data?.length > 0) {
      console.log('TimeSeriesChart: No chart data generated despite having time series data');
    }

    return fullTimeWindow;
  }, [data, sources, selectedDuration, bucketDuration, chartEndTime]);

  const chartDomain = useMemo(() => {
    return [chartEndTime - selectedDuration, chartEndTime];
  }, [chartEndTime, selectedDuration]);

  // Handle click on the chart
  const handleChartClick = useCallback((data) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedData = data.activePayload[0].payload;

      // Validate the data before setting it
      if (clickedData && typeof clickedData.timestamp === 'number' && !isNaN(clickedData.timestamp)) {
        setSelectedPoint(clickedData);
        setSelectedRange(null);
        if (onPointClick) {
          onPointClick(clickedData);
        }
      } else {
        console.warn('Invalid chart data clicked:', clickedData);
      }
    }
  }, [onPointClick]);

  const handleChartMouseDown = useCallback((chartState) => {
    const timestamp = Number(chartState?.activeLabel);

    if (!Number.isFinite(timestamp)) {
      return;
    }

    didDragRef.current = false;
    setDragRange({ startTimestamp: timestamp, endTimestamp: timestamp });
  }, []);

  const handleChartMouseMove = useCallback((chartState) => {
    const timestamp = Number(chartState?.activeLabel);

    if (!dragRange || !Number.isFinite(timestamp)) {
      return;
    }

    if (timestamp !== dragRange.startTimestamp) {
      didDragRef.current = true;
    }

    setDragRange(currentRange => currentRange
      ? { ...currentRange, endTimestamp: timestamp }
      : currentRange);
  }, [dragRange]);

  const handleChartMouseUp = useCallback((chartState) => {
    if (!dragRange) {
      return;
    }

    const activeTimestamp = Number(chartState?.activeLabel);
    const endTimestamp = Number.isFinite(activeTimestamp)
      ? activeTimestamp
      : dragRange.endTimestamp;

    setDragRange(null);

    if (!didDragRef.current || dragRange.startTimestamp === endTimestamp) {
      return;
    }

    const nextRange = {
      startTimestamp: Math.min(dragRange.startTimestamp, endTimestamp),
      endTimestamp: Math.max(dragRange.startTimestamp, endTimestamp) + bucketDuration,
      bucketDuration
    };

    setSelectedRange(nextRange);
    setSelectedPoint(null);

    if (onRangeSelect) {
      onRangeSelect(nextRange);
    }
  }, [bucketDuration, dragRange, onRangeSelect]);

  const handleChartMouseLeave = useCallback(() => {
    if (dragRange) {
      setDragRange(null);
      didDragRef.current = false;
    }
  }, [dragRange]);

  const handleClearSelection = useCallback(() => {
    setSelectedPoint(null);
    setSelectedRange(null);
    setDragRange(null);

    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  useEffect(() => {
    if (previousBucketDurationRef.current !== bucketDuration) {
      previousBucketDurationRef.current = bucketDuration;
      handleClearSelection();
    }
  }, [bucketDuration, handleClearSelection]);

  const handleDurationChange = useCallback((event) => {
    handleClearSelection();
    onDurationChange?.(parseInt(event.target.value));
  }, [handleClearSelection, onDurationChange]);

  const resolvedDurationOptions = durationOptions.length > 0 ? durationOptions : [
    { value: 30 * 1000, label: '30 sec' },
    { value: 1 * 60 * 1000, label: '1 min' },
    { value: 5 * 60 * 1000, label: '5 min' },
    { value: 10 * 60 * 1000, label: '10 min' },
    { value: 30 * 60 * 1000, label: '30 min' },
    { value: 60 * 60 * 1000, label: '1 hr' }
  ];

  const selectedSourceSet = useMemo(() => {
    return new Set(selectedSourceIds);
  }, [selectedSourceIds]);

  const visibleSources = useMemo(() => {
    if (!Array.isArray(selectedSourceIds) || selectedSourceIds.length === 0) {
      return sources;
    }

    return sources.filter(source => selectedSourceSet.has(source.id));
  }, [selectedSourceIds, selectedSourceSet, sources]);

  // Generate lines for each source - memoized to prevent re-renders
  const lineElements = useMemo(() => {
    return visibleSources.map(source => (
      <Line
        key={source.id}
        type="monotone"
        dataKey={source.id}
        name={source.name}
        stroke={sourceColors[source.id] || '#8884d8'}
        strokeWidth={visibleSources.length === 1 ? 2 : 1.5}
        activeDot={{ r: visibleSources.length === 1 ? 5 : 4, strokeWidth: 1 }}
        dot={{
          r: visibleSources.length === 1 ? 2 : 1.5,
          fill: sourceColors[source.id] || '#8884d8',
          strokeWidth: 0
        }}
        isAnimationActive={false}
        connectNulls={false}
      />
    ));
  }, [visibleSources, sourceColors]);

  // Custom tooltip to show all values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p className="label" style={{ margin: 0 }}>
            {`Time: ${formatChartTime(Number(label))}`}
          </p>
          <p className="total" style={{ margin: 0, fontWeight: 'bold' }}>
            {`Total: ${payload.reduce((sum, entry) => sum + (entry.value || 0), 0)}`}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{
              margin: 0,
              color: entry.color
            }}>
              {`${entry.name}: ${entry.value || 0}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const highlightedRange = dragRange
    ? {
        startTimestamp: Math.min(dragRange.startTimestamp, dragRange.endTimestamp),
        endTimestamp: Math.max(dragRange.startTimestamp, dragRange.endTimestamp) + bucketDuration
      }
    : selectedRange;

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Message Rate by Source</h5>
        <div className="d-flex gap-3 align-items-center">
          <small className="text-muted d-none d-md-inline">
            Drag across the graph to select a time range
          </small>
          {(selectedPoint || selectedRange) && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearSelection}
            >
              Return to Live
            </Button>
          )}
          <div className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-muted small fw-bold">Time Window:</Form.Label>
            <Form.Select
              value={selectedDuration}
              onChange={handleDurationChange}
              style={{ width: 'auto' }}
            >
              {resolvedDurationOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

        </div>
      </Card.Header>
      <Card.Body>
        <div style={{ width: '100%', height: 380, minHeight: 380 }}>

          {chartData.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <p className="text-muted">No chart data available</p>
            </div>
          ) : (
            <div
              className="monitoring-time-series-chart"
              style={{ width: '100%', height: '380px' }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{
                    top: 25,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }}
                  onClick={handleChartClick}
                  onMouseDown={handleChartMouseDown}
                  onMouseMove={handleChartMouseMove}
                  onMouseUp={handleChartMouseUp}
                  onMouseLeave={handleChartMouseLeave}
                >
                  <CartesianGrid strokeDasharray="2 2" stroke="#e0e0e0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 11 }}
                    type="number"
                    scale="time"
                    domain={chartDomain}
                    tickCount={7}
                    tickFormatter={formatChartTime}
                    tickMargin={8}
                    angle={0}
                    textAnchor="middle"
                    height={35}
                    allowDataOverflow
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    domain={[0, (dataMax) => Math.max(dataMax, 1)]}
                    label={{
                      value: 'Messages',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {lineElements}
                  {highlightedRange && (
                    <ReferenceArea
                      x1={highlightedRange.startTimestamp}
                      x2={highlightedRange.endTimestamp}
                      fill="#0d6efd"
                      fillOpacity={0.18}
                      stroke="#0d6efd"
                      strokeOpacity={0.75}
                      ifOverflow="hidden"
                    />
                  )}
                  {selectedPoint && (
                    <ReferenceLine
                      x={selectedPoint.timestamp}
                      stroke="red"
                      strokeDasharray="3 3"
                      label={{ value: 'Selected', position: 'top' }}
                      ifOverflow="hidden"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      </Card.Body>
    </Card>
  );
};

export default TimeSeriesChart;
